import React, { useReducer, useEffect, useCallback } from 'react'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import map from 'lodash/map'
import { length } from 'stringz'
import { gql, useMutation } from '@apollo/client'
import { AutoSave, AutoSaveVariables } from '@apollo-types/AutoSave'
import {
  AutosaveStatusType,
  PublishButtonStatusType,
  ArticleState,
  Warning,
} from './article-state.models'
import { useGraphQLErrorsHandler } from './useGraphQLErrorsHandler'
import { PublishStatus } from '@apollo-types/globalTypes'
import { CreateDraftArticle } from '@apollo-types/CreateDraftArticle'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'
import {
  ArticleContentItemShape,
  ParagraphBlockDataType,
} from '../text-input/content-input/version-a/content-input.models'
import { PublishArticle, PublishArticleVariables } from '@apollo-types/PublishArticle'
import { postUrl } from '@libs/appUrl'
import { createReducer } from '@reduxjs/toolkit'
import { EditingArticle_editingArticle } from '@apollo-types/EditingArticle'
import { TITLE_MAX_LENGTH } from '../publish-setting/_shared'
import { openPost, referralAndOpen } from '@libs/appUrl'
import { useRouter } from 'next/router'

const AUTO_SAVE = gql`
  mutation AutoSave(
    $id: ID!
    $title: String
    $content: JSON
    $categoryIds: [ID!]
    $imageIds: [ID!]
    $coverImageId: ID
    $checkInLocationId: ID
    $ogTitle: String
    $ogDescription: String
    $ogImageId: ID
  ) {
    autosaveArticle(
      id: $id
      input: {
        title: $title
        content: $content
        categoryIds: $categoryIds
        imageIds: $imageIds
        coverImageId: $coverImageId
        checkInLocationId: $checkInLocationId
        ogTitle: $ogTitle
        ogDescription: $ogDescription
        ogImageId: $ogImageId
      }
    ) {
      id
      postID
      title
      content
      categories {
        id
      }
      checkInLocation {
        id
      }
      images {
        id
      }
      coverImageId
      ogTitle
      ogDescription
      ogImageId
    }
  }
`
const CREATE_DRAFT_ARTICLE = gql`
  mutation CreateDraftArticle {
    createArticle(input: {}) {
      id
    }
  }
`

type ArticleProviderProps = {
  children: React.ReactNode
  debounceTime?: number
  editingArticle: Partial<EditingArticle_editingArticle>
}

type ArticleAction = {
  type: ActionType
  value?: any
  target?: 'upload_content_image' | 'upload_cover_image'
  timestamp?: number
  warnings?: Record<Warning, boolean>
  action?: string
}

export enum ActionType {
  UPDATE_ID = 'UPDATE_ID',
  UPDATE_TITLE = 'UPDATE_TITLE',
  UPDATE_CONTENT = 'UPDATE_CONTENT',
  UPDATE_SELECT_CATEGORY = 'UPDATE_SELECT_CATEGORY',
  UPDATE_IMAGE_IDS = 'UPDATE_IMAGE_IDS',
  UPDATE_COVER_IMAGE_ID = 'UPDATE_COVER_IMAGE_ID',
  UPDATE_SELECT_LOCATION = 'UPDATE_SELECT_LOCATION',
  UPDATE_OG_TITLE = 'UPDATE_OG_TITLE',
  UPDATE_OG_DESCRIPTION = 'UPDATE_OG_DESCRIPTION',
  UPDATE_OG_IMAGE_ID = 'UPDATE_OG_IMAGE_ID',
  UPDATE_ARTICLE_SAVED_STATUS = 'UPDATE_ARTICLE_SAVED_STATUS',
  AUTOSAVE_SUCCEEDED = 'AUTOSAVE_SUCCEEDED',
  AUTOSAVE_FAILED = 'AUTOSAVE_FAILED',
  PUBLISH_STATUS_CHANGED = 'PUBLISH_STATUS_CHANGED',
  INPUT_INVALIDATED = 'INPUT_INVALIDATED',
  CLEAR_TITLE_CONTENT_REQUIRED_WARNINGS = 'CLEAR_TITLE_CONTENT_REQUIRED_WARNINGS',
}
export interface ArticleContextShape {
  ActionType: typeof ActionType
  AutosaveStatusType: typeof AutosaveStatusType
  PublishButtonStatusType: typeof PublishButtonStatusType
  PublishStatusType: typeof PublishStatus
  articleState: Partial<ArticleState>
  dispatch: React.Dispatch<ArticleAction>
  updateSelectCategory: (value: any) => void
  updateTitleInputChange: (value: any) => void
  updateContentInputChange: (value: any) => void
  updateSelectLocation: (value: any) => void
  updateImageIds: (value: any) => void
  updateCoverImageId: (value: any) => void
  updateOgImageId: (value: any) => void
  publishArticle: () => void
  previewDraftArticle: () => void
  scheduledArticle: () => void
  checkInputWarnings: () => Record<Warning, boolean>
  autosave: () => Promise<unknown>
  clearTitleContentRequiredWarnings: () => void
}

export const ArticleContext = React.createContext<Partial<ArticleContextShape>>({})

// FIXME: 分開 autosaveArticle 和 editingArticle 的 state 應該會比較乾淨，不用一直做 mapping
export const makeAutosaveData = (input: Partial<ArticleState>) => {
  const result = {
    id: '',
    title: '',
    content: [],
    categoryIds: [],
    imageIds: [],
    checkInLocationId: '',
    coverImageId: '',
    ogTitle: '',
    ogDescription: '',
    ogImageId: '',
  }
  for (const key in input) {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      if (key in result) {
        result[key] = input[key]
      }
    }
  }
  return result
}

export const initialArticleState: Partial<ArticleState> = {
  id: '',
  title: '',
  content: [],
  categoryIds: [],
  categoryId: '',
  imageIds: [],
  coverImageId: null,
  checkInLocationId: null,
  ogTitle: null,
  ogDescription: null,
  ogImageId: null,
  scheduledAt: '',
  autosaveStatus: AutosaveStatusType.IDLE,
  publish: {
    status: PublishButtonStatusType.DISABLED,
    warnings: {},
  },
}

export const reducer = createReducer(initialArticleState, {
  [ActionType.UPDATE_ID]: (state, action) => {
    state.id = action.value
  },
  [ActionType.UPDATE_TITLE]: (state, action) => {
    state.autosaveStatus = AutosaveStatusType.PENDING
    state.title = action.value
    delete state.publish.warnings.titleRequired
    if (validateTitleLength({ title: state.title })) {
      delete state.publish.warnings.titleLengthLimited
    }
  },
  [ActionType.UPDATE_CONTENT]: (state, action) => {
    state.autosaveStatus = AutosaveStatusType.PENDING
    state.content = action.value
    delete state.publish.warnings.contentRequired
  },
  [ActionType.UPDATE_SELECT_CATEGORY]: (state, action) => {
    state.autosaveStatus = AutosaveStatusType.PENDING
    state.categoryIds = [action.value]
    delete state.publish.warnings.categoryRequired
  },
  [ActionType.UPDATE_IMAGE_IDS]: (state, action) => {
    state.autosaveStatus = AutosaveStatusType.PENDING
    state.imageIds = [...action.value]
    if (!state.coverImageId) {
      state.coverImageId = action.value[0] || null
    }
    delete state.publish.warnings.imageRequired
  },
  [ActionType.UPDATE_SELECT_LOCATION]: (state, action) => {
    state.autosaveStatus = AutosaveStatusType.PENDING
    state.checkInLocationId = action.value?.id ?? null
    state.checkInLocation = action.value
  },
  [ActionType.UPDATE_COVER_IMAGE_ID]: (state, action) => {
    state.autosaveStatus = AutosaveStatusType.PENDING
    state.coverImageId = action.value
  },
  [ActionType.UPDATE_OG_TITLE]: (state, action) => {
    state.autosaveStatus = AutosaveStatusType.PENDING
    state.ogTitle = action.value
  },
  [ActionType.UPDATE_OG_IMAGE_ID]: (state, action) => {
    state.autosaveStatus = AutosaveStatusType.PENDING
    state.ogImageId = action.value
  },
  [ActionType.UPDATE_OG_DESCRIPTION]: (state, action) => {
    state.autosaveStatus = AutosaveStatusType.PENDING
    state.ogDescription = action.value
  },
  [ActionType.AUTOSAVE_SUCCEEDED]: (state, action) => {
    state.autosaveStatus = AutosaveStatusType.SUCCEEDED
    state.publish.status = PublishButtonStatusType.IDLE
  },
  [ActionType.AUTOSAVE_FAILED]: (state) => {
    state.autosaveStatus = AutosaveStatusType.FAILED
    state.publish.status = PublishButtonStatusType.DISABLED
  },
  [ActionType.PUBLISH_STATUS_CHANGED]: (state, action) => {
    if (!action.value) return
    state.publish.actionTarget = action.target
    state.publish.status = action.value
  },
  [ActionType.INPUT_INVALIDATED]: (state, action) => {
    state.publish.warnings = action.warnings
    state.publish.timestamp = action.timestamp
    state.publish.action = action.action
  },
  [ActionType.CLEAR_TITLE_CONTENT_REQUIRED_WARNINGS]: (state, action) => {
    delete state.publish.warnings.titleRequired
    delete state.publish.warnings.contentRequired
  },
})

const getInitialArticleState = (
  editingArticle: Partial<EditingArticle_editingArticle>,
): Partial<ArticleState> => {
  if (!editingArticle) return initialArticleState

  const images = get(editingArticle, 'images', [])
  const categoryId = get(editingArticle, 'categories[0].id', '')
  return {
    ...initialArticleState,
    id: editingArticle.id,
    title: editingArticle.title,
    content: editingArticle.content,
    categoryIds: categoryId ? [categoryId] : [],
    categoryId,
    imageIds: map(images, 'id'),
    coverImageId: get(editingArticle, 'coverImageId', null),
    checkInLocationId: get(editingArticle, 'checkInLocation.id', null),
    ogTitle: get(editingArticle, 'ogTitle', ''),
    ogDescription: get(editingArticle, 'ogDescription', ''),
    ogImageId: get(editingArticle, 'ogImageId', null),
    status: editingArticle.status,
    scheduledAt: editingArticle.scheduledAt,
  }
}

const ArticleProvider = (props: ArticleProviderProps) => {
  const { children, debounceTime = 1000, editingArticle } = props
  const [autosaveMutation] = useMutation<AutoSave, AutoSaveVariables>(AUTO_SAVE, {
    onCompleted(data) {
      dispatch({ type: ActionType.AUTOSAVE_SUCCEEDED, value: data })
    },
    onError(error) {
      dispatch({ type: ActionType.AUTOSAVE_FAILED, value: error.message })
    },
  })
  const router = useRouter()

  const [createDraftArticleMutation, { loading: isCreatingDraftArticle }] = useMutation<
    CreateDraftArticle
  >(CREATE_DRAFT_ARTICLE, {
    onCompleted(data) {
      dispatch({ type: ActionType.UPDATE_ID, value: data.createArticle.id })
      router.replace(data.createArticle.id, undefined, { shallow: true })
    },
  })

  const [articleState, dispatch] = useReducer(reducer, getInitialArticleState(editingArticle))

  const autosave = useCallback(
    () =>
      autosaveMutation({
        variables: makeAutosaveData(articleState),
      }),
    [autosaveMutation, articleState],
  )

  const autosaveDebounced = useCallback(debounce(autosave, debounceTime), [autosave, debounceTime])

  const errorHandler = useGraphQLErrorsHandler()

  const [publishMutation] = useMutation<PublishArticle, PublishArticleVariables>(PUBLISH_ARTICLE)

  const publishArticle = useCallback(async () => {
    dispatch({ type: ActionType.PUBLISH_STATUS_CHANGED, value: PublishButtonStatusType.LOADING })

    try {
      const { id } = articleState
      await autosaveDebounced.flush()
      const result = await publishMutation({ variables: { id } })
      const { postID } = result.data.publishArticle
      referralAndOpen(postUrl(postID), '/user/me')
    } catch (error) {
      errorHandler(error)
    }

    dispatch({ type: ActionType.PUBLISH_STATUS_CHANGED, value: PublishButtonStatusType.IDLE })
  }, [articleState, publishMutation, errorHandler])

  const checkInputWarnings = useCallback(() => {
    const warnings = checkArticleStateWarnings(articleState)
    if (warnings) {
      dispatch({
        type: ActionType.INPUT_INVALIDATED,
        timestamp: Date.now(),
        warnings,
        action: 'publish',
      })
    }
    return warnings
  }, [articleState])

  const scheduledArticle = useCallback(() => {
    if (window.RNApp) {
      openPost('/creator/post-list')
    } else {
      window.location.assign('/creator/post-list')
    }
    window.sessionStorage.setItem('popdaily:recently-scheduled-article', articleState.id)
  }, [articleState.id])

  const previewDraftArticle = useCallback(() => {
    const warnings = checkInputWarnings()
    if (!warnings) openPost(`/draft/${articleState.id}`)
  }, [checkInputWarnings, articleState.id])

  // 每次有更新就觸發自動儲存草稿
  useUpdateEffect(() => {
    if (isCreatingDraftArticle) return
    if (!articleState.id) {
      createDraftArticleMutation()
      return
    }
    if (articleState.autosaveStatus !== AutosaveStatusType.PENDING) return

    autosaveDebounced()

    return () => autosaveDebounced.cancel()
  }, [autosaveDebounced, articleState.autosaveStatus])

  // 自動儲存草稿完畢前，重新載入頁面會跳出警告視窗
  useEffect(() => {
    if (articleState.autosaveStatus !== AutosaveStatusType.PENDING) return
    const showWarning = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', showWarning)
    return () => {
      window.removeEventListener('beforeunload', showWarning)
    }
  }, [articleState.autosaveStatus])

  const updateSelectCategory = (value) => {
    return dispatch({ type: ActionType.UPDATE_SELECT_CATEGORY, value })
  }

  const updateSelectLocation = (value) => {
    return dispatch({ type: ActionType.UPDATE_SELECT_LOCATION, value })
  }

  const updateTitleInputChange = (event) => {
    const value = event.currentTarget.value
    return dispatch({ type: ActionType.UPDATE_TITLE, value })
  }

  const updateContentInputChange = (value) => {
    return dispatch({ type: ActionType.UPDATE_CONTENT, value })
  }

  const updateCoverImageId = (value) => {
    return dispatch({ type: ActionType.UPDATE_COVER_IMAGE_ID, value })
  }

  const updateOgImageId = (value) => {
    return dispatch({ type: ActionType.UPDATE_OG_IMAGE_ID, value })
  }

  const updateImageIds = (value) => {
    return dispatch({ type: ActionType.UPDATE_IMAGE_IDS, value })
  }

  const clearTitleContentRequiredWarnings = () => {
    return dispatch({ type: ActionType.CLEAR_TITLE_CONTENT_REQUIRED_WARNINGS })
  }

  return (
    <ArticleContext.Provider
      // TODO: 就把 articleState & dispatch & actionType 傳遞下去就好，不需要這些 update function
      value={{
        ActionType,
        AutosaveStatusType,
        PublishButtonStatusType,
        PublishStatusType: PublishStatus,
        articleState,
        dispatch,
        updateSelectCategory,
        updateTitleInputChange,
        updateContentInputChange,
        updateImageIds,
        updateSelectLocation,
        updateCoverImageId,
        updateOgImageId,
        publishArticle,
        previewDraftArticle,
        scheduledArticle,
        checkInputWarnings,
        autosave,
        clearTitleContentRequiredWarnings,
      }}
    >
      {children}
    </ArticleContext.Provider>
  )
}

export default ArticleProvider

const PUBLISH_ARTICLE = gql`
  mutation PublishArticle($id: ID!) {
    publishArticle(id: $id) {
      postID
    }
  }
`

const checkArticleStateWarnings = (articleState: Partial<ArticleState>) => {
  const warnings = {
    [Warning.imageRequired]: validateImage(articleState) === false,
    [Warning.contentRequired]: validateContent(articleState) === false,
    [Warning.categoryRequired]: validateCategory(articleState) === false,
    [Warning.titleRequired]: validateTitle(articleState) === false,
    [Warning.titleLengthLimited]: validateTitleLength(articleState) === false,
  }
  if (Object.values(warnings).includes(true)) return warnings

  return null
}

const validateImage = ({ imageIds }: Partial<ArticleState>) => {
  return Boolean(imageIds?.[0])
}

const validateCategory = ({ categoryIds }: Partial<ArticleState>) => {
  return Boolean(categoryIds?.[0])
}

const validateContent = ({ content }: Partial<{ content: ArticleContentItemShape[] }>) => {
  if (isEmpty(content)) return false
  // 所有段落都是空行的時候不給過
  if (content.every((item) => (item.data as ParagraphBlockDataType).text === '<br>')) return false
  return true
}

const validateTitle = ({ title }: Partial<ArticleState>) => {
  return Boolean(title)
}

const validateTitleLength = ({ title }: Partial<ArticleState>) => {
  return Boolean(length(title) <= TITLE_MAX_LENGTH)
}
